import { CHAPTER_EVENT_SOURCES } from '@app/analytics/constants';
import QueryProgress from '@app/components/query.progress';
import { ChapterEvents } from '@app/domains/chapter/analytics/posthog/events';
import Paywall from '@app/domains/chapter/components/Paywall';
import type { ChangeEvent } from 'react';

export type KarmaPaywallProps = {
    karmaPrice: number;
    autoUnlockEnabled: boolean;
    canChapterUnlockByVoucher: boolean;
    chapterVoucherAmount: number;
    availableVouchers: number;
    isLoading: boolean;
    isKarmaLoading: boolean;
    isVoucherLoading: boolean;
    disabled: boolean;
    shouldEncourageVipFreeNovelSelect: boolean;
    shouldEncourageChampionSubscribe: boolean | undefined;
    onUnlockByKarma: () => void;
    onUnlockByVoucher: () => void;
    onSubscribeChampion: () => void;
    onChangeAutoUnlock: (event: ChangeEvent<HTMLInputElement>) => void;
};
export default function ChapterKarmaPaywall({
    karmaPrice,
    autoUnlockEnabled,
    canChapterUnlockByVoucher,
    chapterVoucherAmount,
    availableVouchers,
    isLoading,
    isKarmaLoading,
    isVoucherLoading,
    disabled,
    shouldEncourageVipFreeNovelSelect,
    shouldEncourageChampionSubscribe,
    onUnlockByKarma,
    onUnlockByVoucher,
    onSubscribeChampion,
    onChangeAutoUnlock,
}: KarmaPaywallProps) {
    return (
        <Paywall>
            <Paywall.TopIcon.Lock />
            <Paywall.Title title="Locked Chapter" />
            <div className="pb-12">
                <Paywall.Desc text="This chapter requires Karma or Subscription." />
            </div>

            <QueryProgress status={isLoading ? 'loading' : 'idle'} />
            {!isLoading && (
                <>
                    {shouldEncourageVipFreeNovelSelect && (
                        <>
                            <div className="mb-12 mt-8 h-1 w-240 bg-gray-line-base" />
                            <Paywall.VipFreeNovelEncourage />
                        </>
                    )}
                    {canChapterUnlockByVoucher && (
                        <Paywall.VoucherPayButton
                            isLoading={isVoucherLoading}
                            disabled={isLoading || disabled}
                            voucherAmount={chapterVoucherAmount}
                            availableVouchers={availableVouchers}
                            data-posthog-click-event={ChapterEvents.ClickByVoucher}
                            data-posthog-params={JSON.stringify({
                                on: CHAPTER_EVENT_SOURCES.VoucherLock,
                                'Voucher Amount': chapterVoucherAmount,
                                'Auto-Unlock': autoUnlockEnabled,
                            })}
                            onClick={onUnlockByVoucher}
                        />
                    )}
                    <Paywall.KarmaPayButton
                        isLoading={isKarmaLoading}
                        disabled={isLoading || disabled}
                        karmaPrice={karmaPrice}
                        data-posthog-click-event={ChapterEvents.ClickByKarma}
                        data-posthog-params={JSON.stringify({
                            on: CHAPTER_EVENT_SOURCES.KarmaLock,
                            'Karma Count': karmaPrice,
                            'Auto-Unlock': autoUnlockEnabled,
                        })}
                        onClick={onUnlockByKarma}
                    />

                    {shouldEncourageChampionSubscribe && (
                        <Paywall.ChampionSubscribeButton
                            disabled={isLoading || disabled}
                            data-posthog-click-event={ChapterEvents.ClickByChampion}
                            data-posthog-params={JSON.stringify({
                                on: CHAPTER_EVENT_SOURCES.KarmaLock,
                                'Auto-Unlock': autoUnlockEnabled,
                            })}
                            onClick={onSubscribeChampion}
                        />
                    )}
                    <div className="pt-14">
                        <Paywall.AutoUnlock defaultChecked={autoUnlockEnabled} onChange={onChangeAutoUnlock} />
                    </div>
                </>
            )}
        </Paywall>
    );
}
